import React, { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Type, Image, MousePointer, Minus, LayoutGrid, Square, AlignLeft,
  AlignCenter, AlignRight, Bold, Italic, Underline, Link, Trash2,
  ChevronUp, ChevronDown, Copy, Eye, Code, Undo, Redo, Save,
  Smartphone, Monitor, GripVertical, Plus, Settings, Palette
} from 'lucide-react';

// Block type definitions
const BLOCK_TYPES = {
  HEADER: 'header',
  TEXT: 'text',
  IMAGE: 'image',
  BUTTON: 'button',
  DIVIDER: 'divider',
  SPACER: 'spacer',
  COLUMNS: 'columns',
  SOCIAL: 'social',
  FOOTER: 'footer',
};

// Default block configurations
const DEFAULT_BLOCKS = {
  [BLOCK_TYPES.HEADER]: {
    type: BLOCK_TYPES.HEADER,
    content: {
      text: 'Your Header Here',
      logoUrl: '',
      showLogo: false,
    },
    styles: {
      backgroundColor: '#4F46E5',
      textColor: '#ffffff',
      padding: 40,
      fontSize: 28,
      textAlign: 'center',
      fontWeight: 'bold',
    }
  },
  [BLOCK_TYPES.TEXT]: {
    type: BLOCK_TYPES.TEXT,
    content: {
      text: 'Add your text content here. You can use personalization like {{first_name}} to make it personal.',
    },
    styles: {
      backgroundColor: '#ffffff',
      textColor: '#333333',
      padding: 20,
      fontSize: 16,
      textAlign: 'left',
      lineHeight: 1.6,
    }
  },
  [BLOCK_TYPES.IMAGE]: {
    type: BLOCK_TYPES.IMAGE,
    content: {
      src: '',
      alt: 'Image description',
      link: '',
    },
    styles: {
      backgroundColor: '#ffffff',
      padding: 10,
      width: 100,
      alignment: 'center',
      borderRadius: 0,
    }
  },
  [BLOCK_TYPES.BUTTON]: {
    type: BLOCK_TYPES.BUTTON,
    content: {
      text: 'Click Here',
      link: '#',
    },
    styles: {
      backgroundColor: '#ffffff',
      buttonColor: '#4F46E5',
      textColor: '#ffffff',
      padding: 20,
      fontSize: 16,
      borderRadius: 6,
      alignment: 'center',
      buttonPadding: '12px 30px',
    }
  },
  [BLOCK_TYPES.DIVIDER]: {
    type: BLOCK_TYPES.DIVIDER,
    content: {},
    styles: {
      backgroundColor: '#ffffff',
      lineColor: '#e5e7eb',
      padding: 20,
      lineWidth: 1,
      lineStyle: 'solid',
    }
  },
  [BLOCK_TYPES.SPACER]: {
    type: BLOCK_TYPES.SPACER,
    content: {},
    styles: {
      height: 30,
      backgroundColor: '#ffffff',
    }
  },
  [BLOCK_TYPES.COLUMNS]: {
    type: BLOCK_TYPES.COLUMNS,
    content: {
      columns: [
        { text: 'Column 1 content' },
        { text: 'Column 2 content' },
      ]
    },
    styles: {
      backgroundColor: '#ffffff',
      padding: 20,
      gap: 20,
      columnCount: 2,
    }
  },
  [BLOCK_TYPES.SOCIAL]: {
    type: BLOCK_TYPES.SOCIAL,
    content: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
    },
    styles: {
      backgroundColor: '#f9fafb',
      padding: 20,
      iconSize: 32,
      alignment: 'center',
    }
  },
  [BLOCK_TYPES.FOOTER]: {
    type: BLOCK_TYPES.FOOTER,
    content: {
      companyName: 'Your Company',
      address: '123 Main St, City, Country',
      unsubscribeText: 'Unsubscribe from this list',
    },
    styles: {
      backgroundColor: '#f9fafb',
      textColor: '#666666',
      padding: 30,
      fontSize: 12,
      textAlign: 'center',
    }
  },
};

// Block palette items
const BLOCK_PALETTE = [
  { type: BLOCK_TYPES.HEADER, label: 'Header', icon: Type, description: 'Logo and title' },
  { type: BLOCK_TYPES.TEXT, label: 'Text', icon: AlignLeft, description: 'Paragraph text' },
  { type: BLOCK_TYPES.IMAGE, label: 'Image', icon: Image, description: 'Add an image' },
  { type: BLOCK_TYPES.BUTTON, label: 'Button', icon: MousePointer, description: 'Call-to-action' },
  { type: BLOCK_TYPES.DIVIDER, label: 'Divider', icon: Minus, description: 'Horizontal line' },
  { type: BLOCK_TYPES.SPACER, label: 'Spacer', icon: Square, description: 'Empty space' },
  { type: BLOCK_TYPES.COLUMNS, label: 'Columns', icon: LayoutGrid, description: '2-column layout' },
  { type: BLOCK_TYPES.FOOTER, label: 'Footer', icon: Type, description: 'Footer section' },
];

export default function EmailBuilder({ 
  initialBlocks = [], 
  onSave, 
  onCancel,
  templateName = '',
  templateSubject = ''
}) {
  const [blocks, setBlocks] = useState(initialBlocks.length > 0 ? initialBlocks : []);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [viewMode, setViewMode] = useState('editor'); // editor, preview, code
  const [draggedBlockType, setDraggedBlockType] = useState(null);
  const [name, setName] = useState(templateName);
  const [subject, setSubject] = useState(templateSubject);
  
  // History management with refs to avoid stale closures
  const historyRef = useRef([JSON.stringify(initialBlocks.length > 0 ? initialBlocks : [])]); // Start with initial state
  const historyIndexRef = useRef(0);
  const [historyVersion, setHistoryVersion] = useState(0); // For triggering re-render on undo/redo

  const saveToHistory = useCallback((newBlocks) => {
    // Only save if state actually changed
    const newState = JSON.stringify(newBlocks);
    if (historyRef.current[historyIndexRef.current] !== newState) {
      const newHistory = [...historyRef.current.slice(0, historyIndexRef.current + 1), newState];
      historyRef.current = newHistory;
      historyIndexRef.current = newHistory.length - 1;
      setHistoryVersion(v => v + 1);
    }
  }, []);

  const undo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current = historyIndexRef.current - 1;
      const prevBlocks = JSON.parse(historyRef.current[historyIndexRef.current]);
      setBlocks(prevBlocks);
      setSelectedBlockIndex(null);
      setHistoryVersion(v => v + 1);
    }
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current = historyIndexRef.current + 1;
      const nextBlocks = JSON.parse(historyRef.current[historyIndexRef.current]);
      setBlocks(nextBlocks);
      setHistoryVersion(v => v + 1);
    }
  }, []);

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  // Add block to canvas
  const addBlock = useCallback((type, index = null) => {
    const newBlock = {
      ...JSON.parse(JSON.stringify(DEFAULT_BLOCKS[type])),
      id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
    
    setBlocks(prev => {
      const newBlocks = [...prev];
      if (index !== null) {
        newBlocks.splice(index, 0, newBlock);
      } else {
        newBlocks.push(newBlock);
      }
      saveToHistory(newBlocks);
      return newBlocks;
    });
    
    setSelectedBlockIndex(index !== null ? index : blocks.length);
  }, [blocks.length]);

  // Update block
  const updateBlock = useCallback((index, updates) => {
    setBlocks(prev => {
      const newBlocks = [...prev];
      newBlocks[index] = { ...newBlocks[index], ...updates };
      saveToHistory(newBlocks);
      return newBlocks;
    });
  }, []);

  // Delete block
  const deleteBlock = useCallback((index) => {
    setBlocks(prev => {
      const newBlocks = prev.filter((_, i) => i !== index);
      saveToHistory(newBlocks);
      return newBlocks;
    });
    setSelectedBlockIndex(null);
  }, []);

  // Move block
  const moveBlock = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= blocks.length) return;
    
    setBlocks(prev => {
      const newBlocks = [...prev];
      const [removed] = newBlocks.splice(fromIndex, 1);
      newBlocks.splice(toIndex, 0, removed);
      saveToHistory(newBlocks);
      return newBlocks;
    });
    setSelectedBlockIndex(toIndex);
  }, [blocks.length]);

  // Duplicate block
  const duplicateBlock = useCallback((index) => {
    const block = blocks[index];
    const newBlock = {
      ...JSON.parse(JSON.stringify(block)),
      id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
    
    setBlocks(prev => {
      const newBlocks = [...prev];
      newBlocks.splice(index + 1, 0, newBlock);
      saveToHistory(newBlocks);
      return newBlocks;
    });
    setSelectedBlockIndex(index + 1);
  }, [blocks, saveToHistory]);

  // Generate HTML from blocks
  const generateHTML = useCallback(() => {
    const renderBlock = (block) => {
      const { type, content, styles } = block;
      
      switch (type) {
        case BLOCK_TYPES.HEADER:
          return `
            <tr>
              <td style="background-color:${styles.backgroundColor};padding:${styles.padding}px;text-align:${styles.textAlign};">
                ${content.showLogo && content.logoUrl ? `<img src="${content.logoUrl}" alt="Logo" style="max-height:50px;margin-bottom:15px;" /><br/>` : ''}
                <h1 style="color:${styles.textColor};font-size:${styles.fontSize}px;font-weight:${styles.fontWeight};margin:0;font-family:Arial,sans-serif;">
                  ${content.text}
                </h1>
              </td>
            </tr>`;
            
        case BLOCK_TYPES.TEXT:
          return `
            <tr>
              <td style="background-color:${styles.backgroundColor};padding:${styles.padding}px;">
                <p style="color:${styles.textColor};font-size:${styles.fontSize}px;text-align:${styles.textAlign};line-height:${styles.lineHeight};margin:0;font-family:Arial,sans-serif;">
                  ${content.text.replace(/\n/g, '<br/>')}
                </p>
              </td>
            </tr>`;
            
        case BLOCK_TYPES.IMAGE:
          const imgHtml = `<img src="${content.src || 'https://via.placeholder.com/600x300?text=Add+Image'}" alt="${content.alt}" style="max-width:${styles.width}%;border-radius:${styles.borderRadius}px;display:block;${styles.alignment === 'center' ? 'margin:0 auto;' : styles.alignment === 'right' ? 'margin-left:auto;' : ''}" />`;
          return `
            <tr>
              <td style="background-color:${styles.backgroundColor};padding:${styles.padding}px;text-align:${styles.alignment};">
                ${content.link ? `<a href="${content.link}">${imgHtml}</a>` : imgHtml}
              </td>
            </tr>`;
            
        case BLOCK_TYPES.BUTTON:
          return `
            <tr>
              <td style="background-color:${styles.backgroundColor};padding:${styles.padding}px;text-align:${styles.alignment};">
                <a href="${content.link}" style="display:inline-block;background-color:${styles.buttonColor};color:${styles.textColor};padding:${styles.buttonPadding};text-decoration:none;border-radius:${styles.borderRadius}px;font-size:${styles.fontSize}px;font-weight:bold;font-family:Arial,sans-serif;">
                  ${content.text}
                </a>
              </td>
            </tr>`;
            
        case BLOCK_TYPES.DIVIDER:
          return `
            <tr>
              <td style="background-color:${styles.backgroundColor};padding:${styles.padding}px;">
                <hr style="border:none;border-top:${styles.lineWidth}px ${styles.lineStyle} ${styles.lineColor};margin:0;" />
              </td>
            </tr>`;
            
        case BLOCK_TYPES.SPACER:
          return `
            <tr>
              <td style="background-color:${styles.backgroundColor};height:${styles.height}px;"></td>
            </tr>`;
            
        case BLOCK_TYPES.COLUMNS:
          const colWidth = Math.floor(100 / styles.columnCount);
          return `
            <tr>
              <td style="background-color:${styles.backgroundColor};padding:${styles.padding}px;">
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    ${content.columns.map((col, i) => `
                      <td width="${colWidth}%" style="padding:0 ${i > 0 ? styles.gap/2 : 0}px 0 ${i < content.columns.length - 1 ? styles.gap/2 : 0}px;vertical-align:top;">
                        <p style="margin:0;font-family:Arial,sans-serif;">${col.text}</p>
                      </td>
                    `).join('')}
                  </tr>
                </table>
              </td>
            </tr>`;
            
        case BLOCK_TYPES.FOOTER:
          return `
            <tr>
              <td style="background-color:${styles.backgroundColor};padding:${styles.padding}px;text-align:${styles.textAlign};">
                <p style="color:${styles.textColor};font-size:${styles.fontSize}px;margin:0 0 10px 0;font-family:Arial,sans-serif;">
                  © ${new Date().getFullYear()} ${content.companyName}. All rights reserved.
                </p>
                <p style="color:${styles.textColor};font-size:${styles.fontSize}px;margin:0 0 10px 0;font-family:Arial,sans-serif;">
                  ${content.address}
                </p>
                <p style="margin:0;">
                  <a href="{{unsubscribe_url}}" style="color:${styles.textColor};font-size:${styles.fontSize}px;font-family:Arial,sans-serif;">
                    ${content.unsubscribeText}
                  </a>
                </p>
              </td>
            </tr>`;
            
        default:
          return '';
      }
    };
    
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject || 'Email'}</title>
</head>
<body style="margin:0;padding:0;background-color:#f5f5f5;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f5f5f5;padding:20px 0;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color:#ffffff;border-radius:8px;overflow:hidden;max-width:600px;">
          ${blocks.map(renderBlock).join('')}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  }, [blocks, subject]);

  // Handle save
  const handleSave = () => {
    if (!name) {
      toast.error('Please enter a template name');
      return;
    }
    const html = generateHTML();
    onSave({
      name,
      subject,
      html_content: html,
      blocks: blocks, // Save blocks for future editing
    });
  };

  // Drag and drop handlers
  const handleDragStart = (type) => {
    setDraggedBlockType(type);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedBlockType) {
      addBlock(draggedBlockType, index);
      setDraggedBlockType(null);
    }
  };

  const selectedBlock = selectedBlockIndex !== null ? blocks[selectedBlockIndex] : null;

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="email-builder">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 border-b bg-white">
        <div className="flex items-center gap-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Template Name"
            className="w-[200px]"
          />
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject Line"
            className="w-[250px]"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={undo} disabled={!canUndo}>
            <Undo className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={redo} disabled={!canRedo}>
            <Redo className="h-4 w-4" />
          </Button>
          <div className="w-px h-6 bg-gray-200 mx-2" />
          <Button
            variant={previewMode === 'desktop' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPreviewMode('desktop')}
          >
            <Monitor className="h-4 w-4" />
          </Button>
          <Button
            variant={previewMode === 'mobile' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPreviewMode('mobile')}
          >
            <Smartphone className="h-4 w-4" />
          </Button>
          <div className="w-px h-6 bg-gray-200 mx-2" />
          <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
            <TabsList className="h-8">
              <TabsTrigger value="editor" className="text-xs px-3">
                <Settings className="h-3 w-3 mr-1" />
                Editor
              </TabsTrigger>
              <TabsTrigger value="preview" className="text-xs px-3">
                <Eye className="h-3 w-3 mr-1" />
                Preview
              </TabsTrigger>
              <TabsTrigger value="code" className="text-xs px-3">
                <Code className="h-3 w-3 mr-1" />
                HTML
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Template
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {viewMode === 'editor' && (
          <>
            {/* Block Palette - Left Sidebar */}
            <div className="w-64 border-r bg-gray-50 p-4 overflow-y-auto">
              <h3 className="font-semibold text-sm mb-3 text-gray-700">Content Blocks</h3>
              <div className="space-y-2">
                {BLOCK_PALETTE.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.type}
                      draggable
                      onDragStart={() => handleDragStart(item.type)}
                      onClick={() => addBlock(item.type)}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 cursor-move hover:border-blue-400 hover:shadow-sm transition-all"
                    >
                      <div className="p-2 bg-blue-50 rounded">
                        <Icon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{item.label}</p>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Canvas - Center */}
            <div className="flex-1 bg-gray-100 overflow-y-auto p-6">
              <div 
                className={`mx-auto bg-white shadow-lg rounded-lg overflow-hidden transition-all ${
                  previewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-[600px]'
                }`}
              >
                {blocks.length === 0 ? (
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 0)}
                    className="h-64 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200 m-4 rounded-lg"
                  >
                    <Plus className="h-8 w-8 mb-2" />
                    <p className="text-sm">Drag blocks here or click to add</p>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => addBlock(BLOCK_TYPES.HEADER)}
                      className="mt-2"
                    >
                      Start with a header
                    </Button>
                  </div>
                ) : (
                  blocks.map((block, index) => (
                    <div key={block.id}>
                      {/* Drop zone before block */}
                      <div
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        className="h-2 hover:h-8 hover:bg-blue-100 transition-all"
                      />
                      
                      {/* Block */}
                      <div
                        onClick={() => setSelectedBlockIndex(index)}
                        className={`relative group cursor-pointer ${
                          selectedBlockIndex === index ? 'ring-2 ring-blue-500' : ''
                        }`}
                      >
                        {/* Block Controls */}
                        <div className={`absolute -left-10 top-1/2 -translate-y-1/2 flex flex-col gap-1 ${
                          selectedBlockIndex === index ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        } transition-opacity`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); moveBlock(index, index - 1); }}
                            disabled={index === 0}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <div className="h-7 w-7 flex items-center justify-center cursor-grab">
                            <GripVertical className="h-4 w-4 text-gray-400" />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); moveBlock(index, index + 1); }}
                            disabled={index === blocks.length - 1}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Block actions */}
                        <div className={`absolute -right-10 top-1/2 -translate-y-1/2 flex flex-col gap-1 ${
                          selectedBlockIndex === index ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        } transition-opacity`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); duplicateBlock(index); }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                            onClick={(e) => { e.stopPropagation(); deleteBlock(index); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Block Preview */}
                        <BlockPreview block={block} />
                      </div>
                    </div>
                  ))
                )}
                
                {/* Drop zone at the end */}
                {blocks.length > 0 && (
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, blocks.length)}
                    className="h-8 hover:h-16 hover:bg-blue-100 transition-all flex items-center justify-center text-gray-400 text-sm"
                  >
                    <span className="opacity-0 hover:opacity-100">Drop block here</span>
                  </div>
                )}
              </div>
            </div>

            {/* Properties Panel - Right Sidebar */}
            <div className="w-72 border-l bg-white overflow-y-auto">
              {selectedBlock ? (
                <BlockProperties
                  block={selectedBlock}
                  onChange={(updates) => updateBlock(selectedBlockIndex, updates)}
                />
              ) : (
                <div className="p-6 text-center text-gray-400">
                  <Palette className="h-8 w-8 mx-auto mb-2" />
                  <p className="text-sm">Select a block to edit its properties</p>
                </div>
              )}
            </div>
          </>
        )}

        {viewMode === 'preview' && (
          <div className="flex-1 bg-gray-100 overflow-y-auto p-6">
            <div className={`mx-auto shadow-lg rounded-lg overflow-hidden ${
              previewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-[600px]'
            }`}>
              <iframe
                srcDoc={generateHTML()}
                className="w-full h-[600px] border-0 bg-white"
                title="Email Preview"
              />
            </div>
          </div>
        )}

        {viewMode === 'code' && (
          <div className="flex-1 p-6 overflow-hidden">
            <Textarea
              value={generateHTML()}
              readOnly
              className="w-full h-full font-mono text-xs"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Block Preview Component
function BlockPreview({ block }) {
  const { type, content, styles } = block;
  
  const containerStyle = {
    backgroundColor: styles.backgroundColor,
    padding: `${styles.padding}px`,
  };
  
  switch (type) {
    case BLOCK_TYPES.HEADER:
      return (
        <div style={containerStyle}>
          {content.showLogo && content.logoUrl && (
            <img src={content.logoUrl} alt="Logo" style={{ maxHeight: 50, margin: '0 auto 15px', display: 'block' }} />
          )}
          <h1 style={{
            color: styles.textColor,
            fontSize: styles.fontSize,
            fontWeight: styles.fontWeight,
            textAlign: styles.textAlign,
            margin: 0,
          }}>
            {content.text}
          </h1>
        </div>
      );
      
    case BLOCK_TYPES.TEXT:
      return (
        <div style={containerStyle}>
          <p style={{
            color: styles.textColor,
            fontSize: styles.fontSize,
            textAlign: styles.textAlign,
            lineHeight: styles.lineHeight,
            margin: 0,
            whiteSpace: 'pre-wrap',
          }}>
            {content.text}
          </p>
        </div>
      );
      
    case BLOCK_TYPES.IMAGE:
      return (
        <div style={{ ...containerStyle, textAlign: styles.alignment }}>
          <img
            src={content.src || 'https://via.placeholder.com/600x300?text=Click+to+add+image'}
            alt={content.alt}
            style={{
              maxWidth: `${styles.width}%`,
              borderRadius: styles.borderRadius,
              display: styles.alignment === 'center' ? 'block' : 'inline-block',
              margin: styles.alignment === 'center' ? '0 auto' : undefined,
            }}
          />
        </div>
      );
      
    case BLOCK_TYPES.BUTTON:
      return (
        <div style={{ ...containerStyle, textAlign: styles.alignment }}>
          <button style={{
            backgroundColor: styles.buttonColor,
            color: styles.textColor,
            padding: styles.buttonPadding,
            border: 'none',
            borderRadius: styles.borderRadius,
            fontSize: styles.fontSize,
            fontWeight: 'bold',
            cursor: 'pointer',
          }}>
            {content.text}
          </button>
        </div>
      );
      
    case BLOCK_TYPES.DIVIDER:
      return (
        <div style={containerStyle}>
          <hr style={{
            border: 'none',
            borderTop: `${styles.lineWidth}px ${styles.lineStyle} ${styles.lineColor}`,
            margin: 0,
          }} />
        </div>
      );
      
    case BLOCK_TYPES.SPACER:
      return <div style={{ height: styles.height, backgroundColor: styles.backgroundColor }} />;
      
    case BLOCK_TYPES.COLUMNS:
      return (
        <div style={{ ...containerStyle, display: 'flex', gap: styles.gap }}>
          {content.columns.map((col, i) => (
            <div key={i} style={{ flex: 1 }}>
              <p style={{ margin: 0 }}>{col.text}</p>
            </div>
          ))}
        </div>
      );
      
    case BLOCK_TYPES.FOOTER:
      return (
        <div style={containerStyle}>
          <p style={{ color: styles.textColor, fontSize: styles.fontSize, textAlign: styles.textAlign, margin: '0 0 10px' }}>
            © {new Date().getFullYear()} {content.companyName}. All rights reserved.
          </p>
          <p style={{ color: styles.textColor, fontSize: styles.fontSize, textAlign: styles.textAlign, margin: '0 0 10px' }}>
            {content.address}
          </p>
          <p style={{ color: styles.textColor, fontSize: styles.fontSize, textAlign: styles.textAlign, margin: 0 }}>
            <a href="#" style={{ color: styles.textColor }}>{content.unsubscribeText}</a>
          </p>
        </div>
      );
      
    default:
      return <div style={containerStyle}>Unknown block type</div>;
  }
}

// Block Properties Panel Component
function BlockProperties({ block, onChange }) {
  const { type, content, styles } = block;
  
  const updateContent = (key, value) => {
    onChange({ content: { ...content, [key]: value } });
  };
  
  const updateStyles = (key, value) => {
    onChange({ styles: { ...styles, [key]: value } });
  };
  
  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-6">
        <div className="flex items-center gap-2 pb-3 border-b">
          <span className="font-semibold text-sm capitalize">{type} Block</span>
        </div>
        
        {/* Content Settings */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Content</h4>
          
          {type === BLOCK_TYPES.HEADER && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Header Text</Label>
                <Input
                  value={content.text}
                  onChange={(e) => updateContent('text', e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={content.showLogo}
                  onChange={(e) => updateContent('showLogo', e.target.checked)}
                  className="rounded"
                />
                <Label className="text-xs">Show Logo</Label>
              </div>
              {content.showLogo && (
                <div>
                  <Label className="text-xs">Logo URL</Label>
                  <Input
                    value={content.logoUrl}
                    onChange={(e) => updateContent('logoUrl', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              )}
            </div>
          )}
          
          {type === BLOCK_TYPES.TEXT && (
            <div>
              <Label className="text-xs">Text Content</Label>
              <Textarea
                value={content.text}
                onChange={(e) => updateContent('text', e.target.value)}
                rows={5}
              />
              <p className="text-xs text-gray-500 mt-1">
                Use {"{{first_name}}"}, {"{{company}}"} for personalization
              </p>
            </div>
          )}
          
          {type === BLOCK_TYPES.IMAGE && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Image URL</Label>
                <Input
                  value={content.src}
                  onChange={(e) => updateContent('src', e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label className="text-xs">Alt Text</Label>
                <Input
                  value={content.alt}
                  onChange={(e) => updateContent('alt', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Link URL</Label>
                <Input
                  value={content.link}
                  onChange={(e) => updateContent('link', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
          )}
          
          {type === BLOCK_TYPES.BUTTON && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Button Text</Label>
                <Input
                  value={content.text}
                  onChange={(e) => updateContent('text', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Link URL</Label>
                <Input
                  value={content.link}
                  onChange={(e) => updateContent('link', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
          )}
          
          {type === BLOCK_TYPES.FOOTER && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Company Name</Label>
                <Input
                  value={content.companyName}
                  onChange={(e) => updateContent('companyName', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Address</Label>
                <Input
                  value={content.address}
                  onChange={(e) => updateContent('address', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Unsubscribe Text</Label>
                <Input
                  value={content.unsubscribeText}
                  onChange={(e) => updateContent('unsubscribeText', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Style Settings */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Styling</h4>
          
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Background Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={styles.backgroundColor}
                  onChange={(e) => updateStyles('backgroundColor', e.target.value)}
                  className="w-10 h-8 rounded cursor-pointer"
                />
                <Input
                  value={styles.backgroundColor}
                  onChange={(e) => updateStyles('backgroundColor', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            
            {styles.textColor !== undefined && (
              <div>
                <Label className="text-xs">Text Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={styles.textColor}
                    onChange={(e) => updateStyles('textColor', e.target.value)}
                    className="w-10 h-8 rounded cursor-pointer"
                  />
                  <Input
                    value={styles.textColor}
                    onChange={(e) => updateStyles('textColor', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            )}
            
            {type === BLOCK_TYPES.BUTTON && (
              <div>
                <Label className="text-xs">Button Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={styles.buttonColor}
                    onChange={(e) => updateStyles('buttonColor', e.target.value)}
                    className="w-10 h-8 rounded cursor-pointer"
                  />
                  <Input
                    value={styles.buttonColor}
                    onChange={(e) => updateStyles('buttonColor', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            )}
            
            {styles.padding !== undefined && (
              <div>
                <Label className="text-xs">Padding: {styles.padding}px</Label>
                <Slider
                  value={[styles.padding]}
                  onValueChange={([v]) => updateStyles('padding', v)}
                  min={0}
                  max={60}
                  step={5}
                />
              </div>
            )}
            
            {styles.fontSize !== undefined && (
              <div>
                <Label className="text-xs">Font Size: {styles.fontSize}px</Label>
                <Slider
                  value={[styles.fontSize]}
                  onValueChange={([v]) => updateStyles('fontSize', v)}
                  min={10}
                  max={48}
                  step={1}
                />
              </div>
            )}
            
            {styles.textAlign !== undefined && (
              <div>
                <Label className="text-xs">Text Alignment</Label>
                <div className="flex gap-1 mt-1">
                  {['left', 'center', 'right'].map((align) => (
                    <Button
                      key={align}
                      variant={styles.textAlign === align ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateStyles('textAlign', align)}
                      className="flex-1"
                    >
                      {align === 'left' && <AlignLeft className="h-4 w-4" />}
                      {align === 'center' && <AlignCenter className="h-4 w-4" />}
                      {align === 'right' && <AlignRight className="h-4 w-4" />}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {styles.borderRadius !== undefined && (
              <div>
                <Label className="text-xs">Border Radius: {styles.borderRadius}px</Label>
                <Slider
                  value={[styles.borderRadius]}
                  onValueChange={([v]) => updateStyles('borderRadius', v)}
                  min={0}
                  max={30}
                  step={1}
                />
              </div>
            )}
            
            {type === BLOCK_TYPES.SPACER && (
              <div>
                <Label className="text-xs">Height: {styles.height}px</Label>
                <Slider
                  value={[styles.height]}
                  onValueChange={([v]) => updateStyles('height', v)}
                  min={10}
                  max={100}
                  step={5}
                />
              </div>
            )}
            
            {type === BLOCK_TYPES.IMAGE && (
              <div>
                <Label className="text-xs">Width: {styles.width}%</Label>
                <Slider
                  value={[styles.width]}
                  onValueChange={([v]) => updateStyles('width', v)}
                  min={20}
                  max={100}
                  step={5}
                />
              </div>
            )}
            
            {type === BLOCK_TYPES.DIVIDER && (
              <>
                <div>
                  <Label className="text-xs">Line Color</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={styles.lineColor}
                      onChange={(e) => updateStyles('lineColor', e.target.value)}
                      className="w-10 h-8 rounded cursor-pointer"
                    />
                    <Input
                      value={styles.lineColor}
                      onChange={(e) => updateStyles('lineColor', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Line Width: {styles.lineWidth}px</Label>
                  <Slider
                    value={[styles.lineWidth]}
                    onValueChange={([v]) => updateStyles('lineWidth', v)}
                    min={1}
                    max={5}
                    step={1}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
